import { Formik } from 'formik';
import { FC, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { FormModal, GFormFields } from '@components';
import { domainFormSchema, getDomainFormConfig } from '@constants';
import { Dictionaries, FormActionType, FormType, originType } from '@enums';
import { useAppDispatch, useFormContent, useFormProcedure } from '@hooks';
import { getCdnSummary, submitFormRequest, useCdnSummary } from '@store';
import i18n from '@translations';
import type { DialogBase, DomainDataProps, FormConfig } from '@types';
import { getOptionListFromCatalog, validateValueEqual } from '@utils';

export const DomainModal: FC<DialogBase> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const { id: application_id } = useParams<{ id: string }>();

  const formType = FormType.CdnDomain;

  const currentForm = useFormContent({ formType });
  const currentProcedure = useFormProcedure(formType);
  const appConfigurationDetails = useCdnSummary();

  const isEdit = validateValueEqual(currentProcedure, FormActionType.Edit);

  const allowWildCard = appConfigurationDetails?.allow_wildcard;
  const schema = useMemo(() => domainFormSchema({ allowWildCard }), [allowWildCard]);
  const originOptionList = getOptionListFromCatalog({ name: Dictionaries.OriginType });

  const formConfig: FormConfig = useMemo(
    () =>
      getDomainFormConfig({
        isEdit,
        originOptionList,
        schema,
      }),
    [isEdit, originOptionList, schema, i18n.language],
  );

  const initialValues: DomainDataProps = useMemo(
    () => ({
      domain: currentForm?.domain ?? '',
      name: currentForm?.name ?? '',
      origin: currentForm?.origin ?? '',
      type: currentForm?.type ?? originType.HTTPS,
      connect_timeout: currentForm?.connect_timeout ?? 5,
      send_timeout: currentForm?.send_timeout ?? 5,
      read_timeout: currentForm?.read_timeout ?? 5,
      verify_ssl_cert: currentForm?.verify_ssl_cert ?? true,
    }),
    [currentForm],
  );

  const handleSubmit = useCallback(
    (values: DomainDataProps): void => {
      const { domain, name, origin, type, verify_ssl_cert, connect_timeout, send_timeout, read_timeout } = values;

      const formData = {
        domain,
        name,
        ...(origin
          ? {
              type,
              origin,
              verify_ssl_cert: type === originType.HTTPS ? verify_ssl_cert : false,
              connect_timeout,
              send_timeout,
              read_timeout,
            }
          : {}),
        ...(!isEdit && { application_id }),
      };

      dispatch(
        submitFormRequest({
          formType,
          formData,
          ...(isEdit && {
            params: {
              content_id: currentForm?.id,
            },
          }),
          globalOptions: { refetch_id: application_id },
          callbackFunction: { refetch: getCdnSummary(application_id) },
        }),
      );
    },
    [dispatch, application_id, currentForm?.id, isEdit, formType],
  );

  const { inputList, validationSchema, label } = formConfig;

  if (!open || !inputList) return null;

  return (
    <Formik<DomainDataProps>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ touched, errors }) => (
        <FormModal
          formType={formType}
          title={label?.title}
          description={label?.description}
          onClose={onClose}
          size="lg"
          open={open}
          confirmButton={{ children: label?.confirmButton }}
        >
          <GFormFields inputList={inputList} touched={touched} errors={errors} />
        </FormModal>
      )}
    </Formik>
  );
};
